import React from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { GraphDataParam } from '../../types/enums/graphDataParam'
import { getGraphLimits, getValueSuffix } from '../../utils/graphUtils'
import NumberField from '../fields/numberField'
import { GraphDatapoint } from '../../types/graphSeriesConfig'
import {
  getSeriesLabel,
  movePointsDown,
  movePointsLeft,
  movePointsRight,
  movePointsUp,
} from '../../utils/profileEditorUtils'
import { SeriesType } from '../../types/enums/seriesType'

enum AdjustType {
  FixedTime = 'fixedTime',
  PercentTime = 'percentTime',
  FixedValue = 'fixedValue',
  PercentValue = 'percentValue',
}

enum AdjustSign {
  Plus = '+',
  Minus = '-',
}

interface AdjustValuesModalProps {
  editParam: GraphDataParam
  pointsArray: Array<GraphDatapoint>
  selectedPointIndexes: Array<number>
  valueSuffix: string
  adjustNumberLabel: string
  maxX: number
  moveIntervalX: number
  isXaxisTemp: boolean
  container?: HTMLElement | null
  onClose: () => void
  onDispatch: (pointsArray: Array<GraphDatapoint>) => void
}

const AdjustValuesModal = (props: AdjustValuesModalProps) => {
  const { t } = useTranslation()

  const [adjustType, setAdjustType] = React.useState<AdjustType>(AdjustType.FixedTime)
  const [adjustSign, setAdjustSign] = React.useState<AdjustSign>(AdjustSign.Plus)
  const [adjustValue, setAdjustValue] = React.useState<number>(0)

  const doTimeAdjustment = (saveAdjustValue: number): void => {
    const moveInterval = props.moveIntervalX
    const valueWithSign = adjustSign === AdjustSign.Minus ? saveAdjustValue * -1 : saveAdjustValue
    const adjustFn =
      adjustType === AdjustType.PercentTime
        ? (x: number): number => Math.round((x * (100 + valueWithSign)) / 100 / moveInterval) * moveInterval
        : (x: number): number => x + valueWithSign * moveInterval
    const adjustedArray =
      adjustSign === AdjustSign.Minus
        ? movePointsLeft(props.pointsArray, props.selectedPointIndexes, moveInterval, adjustFn)
        : movePointsRight(props.pointsArray, props.selectedPointIndexes, moveInterval, props.maxX, adjustFn)

    props.onDispatch(adjustedArray)
  }

  const doValueAdjustment = (saveAdjustValue: number): void => {
    const minValue = getGraphLimits(props.editParam).min
    const maxValue = getGraphLimits(props.editParam).max
    const valueWithSign = adjustSign === AdjustSign.Minus ? saveAdjustValue * -1 : saveAdjustValue
    const adjustFn =
      adjustType === AdjustType.PercentValue
        ? (x: number): number => Math.round((x * (100 + valueWithSign)) / 100)
        : (x: number): number => x + valueWithSign

    const adjustedArray =
      adjustSign === AdjustSign.Minus
        ? movePointsDown(props.pointsArray, props.selectedPointIndexes, minValue, adjustFn)
        : movePointsUp(props.pointsArray, props.selectedPointIndexes, maxValue, adjustFn)

    props.onDispatch(adjustedArray)
  }

  const getDialogTitle = (): string => {
    const seriesLabel = t(getSeriesLabel(props.editParam))
    return t('editprofileview.adjustallvaluesby').replace('{0}', props.adjustNumberLabel).replace('{1}', seriesLabel)
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={props.container} onKeyDown={(e) => e.stopPropagation()}>
      <DialogContent>
        <Stack>
          <FormControl component="fieldset">
            <FormLabel component="legend">{getDialogTitle()}</FormLabel>
            <RadioGroup
              aria-label={getDialogTitle()}
              defaultValue={AdjustType.FixedValue}
              name="adjust-values-buttons-group"
              value={adjustType}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                setAdjustValue(0)
                setAdjustType(event.target.value as AdjustType)
              }}
            >
              <FormControlLabel
                value={AdjustType.FixedTime}
                control={<Radio />}
                label={props.isXaxisTemp ? t('editprofileview.fixedbtvalue') : t('editprofileview.fixedtime')}
              />
              {adjustType === AdjustType.FixedTime && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}
                >
                  <Select
                    native
                    size="small"
                    value={adjustSign}
                    inputProps={{ 'aria-label': '+ or - select' }}
                    onChange={(event: SelectChangeEvent) => {
                      setAdjustSign(event.target.value === AdjustSign.Plus ? AdjustSign.Plus : AdjustSign.Minus)
                    }}
                  >
                    <option>{AdjustSign.Plus}</option>
                    <option>{AdjustSign.Minus}</option>
                  </Select>
                  <NumberField
                    valueSuffix={props.isXaxisTemp ? getValueSuffix(SeriesType.TEMPERATURE) : 'sec'}
                    numValue={adjustValue || ''}
                    minNumValue={0}
                    maxNumValue={250}
                    ariaLabel="fixed time"
                    onBlur={setAdjustValue}
                    onEnter={doTimeAdjustment}
                  />
                </Stack>
              )}
              <FormControlLabel
                value={AdjustType.PercentTime}
                control={<Radio />}
                label={props.isXaxisTemp ? t('editprofileview.percentagebtvalue') : t('editprofileview.percenttime')}
              />
              {adjustType === AdjustType.PercentTime && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}
                >
                  <Select
                    native
                    size="small"
                    value={adjustSign}
                    inputProps={{ 'aria-label': '+ or - select' }}
                    onChange={(event: SelectChangeEvent) => {
                      setAdjustSign(event.target.value === AdjustSign.Plus ? AdjustSign.Plus : AdjustSign.Minus)
                    }}
                  >
                    <option>{AdjustSign.Plus}</option>
                    <option>{AdjustSign.Minus}</option>
                  </Select>
                  <NumberField
                    valueSuffix="%"
                    numValue={adjustValue || ''}
                    minNumValue={0}
                    maxNumValue={100}
                    ariaLabel="percent time"
                    onBlur={setAdjustValue}
                    onEnter={doTimeAdjustment}
                  />
                </Stack>
              )}
              <FormControlLabel
                value={AdjustType.FixedValue}
                control={<Radio />}
                label={t('editprofileview.fixedvalue')}
              />
              {adjustType === AdjustType.FixedValue && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}
                >
                  <Select
                    native
                    size="small"
                    value={adjustSign}
                    inputProps={{ 'aria-label': '+ or - select' }}
                    onChange={(event: SelectChangeEvent) => {
                      setAdjustSign(event.target.value === AdjustSign.Plus ? AdjustSign.Plus : AdjustSign.Minus)
                    }}
                  >
                    <option>{AdjustSign.Plus}</option>
                    <option>{AdjustSign.Minus}</option>
                  </Select>
                  <NumberField
                    valueSuffix={props.valueSuffix}
                    numValue={adjustValue || ''}
                    minNumValue={0}
                    maxNumValue={250}
                    ariaLabel="fixed value"
                    onBlur={setAdjustValue}
                    onEnter={doValueAdjustment}
                  />
                </Stack>
              )}
              <FormControlLabel
                value={AdjustType.PercentValue}
                control={<Radio />}
                label={t('editprofileview.percentvalue')}
              />
              {adjustType === AdjustType.PercentValue && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}
                >
                  <Select
                    native
                    size="small"
                    value={adjustSign}
                    inputProps={{ 'aria-label': '+ or - select' }}
                    onChange={(event: SelectChangeEvent) => {
                      setAdjustSign(event.target.value === AdjustSign.Plus ? AdjustSign.Plus : AdjustSign.Minus)
                    }}
                  >
                    <option>{AdjustSign.Plus}</option>
                    <option>{AdjustSign.Minus}</option>
                  </Select>
                  <NumberField
                    valueSuffix="%"
                    numValue={adjustValue || ''}
                    minNumValue={0}
                    maxNumValue={100}
                    ariaLabel="percent value"
                    onBlur={setAdjustValue}
                    onEnter={doValueAdjustment}
                  />
                </Stack>
              )}
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (adjustType === AdjustType.FixedTime || adjustType === AdjustType.PercentTime) {
              doTimeAdjustment(adjustValue)
            } else {
              doValueAdjustment(adjustValue)
            }
          }}
        >
          {t('editprofileview.adjustvalues').replace('{0}', props.adjustNumberLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AdjustValuesModal
